import { useReducer } from 'react';
import {
  availableThemes,
  findTheme,
  themeDefaults,
} from './ThemeSelector/updateTheme';
import { placements } from './components/placements';
import { getGamificationThemeDefaults } from './panels/GamificationPanel/gamificationThemeDefaults';

const reduceOnSelectGoal = (goal) => {
  switch (goal) {
    case 'call':
      return {
        headline: 'Talk to us to find out more',
        link_text: 'Call Now',
      };
    case 'email':
      return {
        headline:
          'Join our mailing list to stay up to date on our upcoming events',
        link_text: 'Subscribe',
      };
    case 'announcement':
      return {
        headline:
          'Flash Sale: 20% Off Sitewide, Enter Code: "20savings" on Checkout!',
        link_text: 'Shop Now',
      };
    case 'social':
      return {
        headline: 'Like us on Facebook!',
      };
    case 'traffic':
      return {
        headline:
          'Want To Become An Expert In Hosting Webinars? Join Our Free Webinar Masterclass!',
        link_text: 'Save Me A Spot!',
      };
    case 'retirement':
      return {
        headline:
          "Tell us a few things about yourself, and our calculator will show whether you're on track for the retirement you want.",
        link_text: 'See If You Are On Track For Retirement',
        conversion_cta_text: "Let's schedule a call (free)",
        element_subtype: 'retirement',
        type: 'Takeover',
        preview_page: null,
        required_fields: true,
        theme_id: 'dark-green-spring',
      };
    case 'leadform':
      return {
        headline: "Let's connect you with a planner.",
        link_text: "Let's get started",
        conversion_cta_text: 'Submit',
        element_subtype: 'leadform',
        type: 'Modal',
        preview_page: null,
        required_fields: true,
        placement: 'middle',
        theme_id: 'dark-green-spring',
      };
    case 'gamification':
      return {
        type: 'Modal',
        placement: 'middle',
        headline: 'Spin to Win! Enter your email for a chance to get exclusive offers.',
        link_text: 'SPIN NOW',
        theme_id: 'gamification-spin',
        gamification: {
          game_type: 'spin_wheel',
          one_play_per_visitor: true,
          collect_email_before: true,
          win_message: "You won! Here's your reward:",
          lose_message: "Better luck next time! We'll keep you posted on future offers.",
          rewards: [
            { label: '20% Off',       probability: 20, coupon_code: 'OFF20',        color: 'E63946' },
            { label: 'Free Shipping', probability: 20, coupon_code: 'FREE_SHIPPING', color: '457B9D' },
            { label: '10% Off',       probability: 20, coupon_code: 'OFF10',        color: 'E9C46A' },
            { label: 'Free Gift',     probability: 15, coupon_code: 'FREE_GIFT',    color: 'F4A261' },
            { label: '15% Off',       probability: 15, coupon_code: 'OFF15',        color: '2A9D8F' },
            { label: 'Sorry',         probability: 10, coupon_code: '',             color: '264653', is_losing: true },
          ],
        },
      };
    case 'forms':
      return {
        type: 'Modal',
        placement: 'middle',
        link_text: 'Submit',
        element_subtype: 'forms',
        subtype: 'forms',
        form_type: 'survey',
        form_questions: [],
      };
    default:
      break;
  }
};

const reduceOnSelectType = (type, previousType, themeId, siteSettings) => {
  const defaultPlacement = () => {
    if (placements[type] && placements[type].length > 0) {
      return { placement: placements[type][0].value };
    }
  };

  const pusher = document.querySelector('#hellobar-pusher');
  if (pusher) {
    pusher.style.display = 'none';
  }

  // Reset showBorder only when switching FROM Inline TO non-Inline layout
  // This prevents custom Inline borders from showing in other layouts
  // Theme default borders (defined in CSS Base styles) are not affected
  let resetBorder = {};
  if (previousType === 'Inline' && type !== 'Inline') {
    // Check if the theme has a default show_border for the new layout type
    // If it does, use the theme default instead of resetting to false
    if (themeId && siteSettings) {
      const theme = findTheme({ theme_id: themeId, type }, siteSettings);
      const defaults = themeDefaults(theme, type);
      if (defaults && defaults.show_border !== undefined) {
        // Theme has a default, use it
        resetBorder = { show_border: defaults.show_border };
      } else {
        // No theme default, reset to false (user-customizable border only works in Inline)
        resetBorder = { show_border: false };
      }
    } else {
      // No theme info available, reset to false
      resetBorder = { show_border: false };
    }
  }

  return { type: type, size: 50, ...defaultPlacement(), ...resetBorder };
};

const reduceTheme = (settings, siteSettings) => {
  const { theme_id, type, subtype } = settings;
  const theme = findTheme({ theme_id, type }, siteSettings);

  function imagePlacement() {
    if (!theme) return settings.image_placement;

    const positionIsSelectable = theme.image.position_selectable;
    const imageIsBackground = settings.image_placement === 'background';
    const positionIsEmpty = settings.image_placement === '';

    if (!positionIsSelectable) {
      return theme.image.position_default;
    } else if (imageIsBackground || positionIsEmpty) {
      return theme.image.position_default;
    } else {
      return settings.image_placement;
    }
  }

  const reset = {
    image_large_url: null,
    image_modal_url: null,
    image_url: null,
    image_file_name: null,
    use_default_image: false,
    media_type: settings.media_type || 'image',
    video_source_type: (settings.video_source_type || 'url') === 'upload' ? 'url' : (settings.video_source_type || 'url'),
    video_url: settings.video_url || null,
    video_large_url: settings.video_large_url || null,
    video_embed_url: settings.video_embed_url || null,
    video_autoplay: settings.video_autoplay ?? false,
    video_muted: settings.video_muted ?? true,
    video_loop: settings.video_loop ?? false,
    video_controls: settings.video_controls ?? true,
    video_poster_url: settings.video_poster_url || null,
    size: 50,
  };

  const defaults = themeDefaults(theme, type, subtype);

  // When theme changes, reset border_radius to the new theme's default (not the previous theme's value).
  // CIO and CIO Plus use 12px default; Paris uses 20px; Forms use 12px; all other themes use 0.
  const isCioTheme = theme_id === 'cio' || theme_id === 'cio-plus';
  const isParisTheme = theme_id === 'paris';
  const isFormsSubtype = subtype === 'forms' || settings.element_subtype === 'forms';

  let defaultRadius = 0;
  if (isCioTheme) defaultRadius = 12;
  if (isParisTheme) defaultRadius = 20;
  if (isFormsSubtype) defaultRadius = 12;

  defaults.border_radius = defaults.border_radius ?? defaultRadius;

  const defaultHeadline =
    settings.theme?.defaults && settings.theme.defaults[type]?.headline;
  const defaultLinkText =
    settings.theme?.defaults && settings.theme.defaults[type]?.link_text;

  if (
    settings.headline &&
    settings.headline !== defaultHeadline &&
    settings.headline !== '' &&
    siteSettings.canUpdateThemeContents
  ) {
    delete defaults.headline;
  }

  if (
    settings.link_text &&
    settings.link_text !== '' &&
    settings.link_text !== 'Click Here' &&
    settings.link_text !== defaultLinkText &&
    siteSettings.canUpdateThemeContents
  ) {
    delete defaults.link_text;
  }

  defaults.use_default_image = !!defaults.theme?.image?.default_url;
  defaults.show_optional_caption =
    Boolean(defaults.caption) && defaults.caption.length > 0;
  defaults.show_optional_content =
    Boolean(defaults.content) && defaults.content.length > 0;

  if (typeof defaults.show_border === 'undefined' || defaults.show_border === null) {
    defaults.show_border = false;
  }
  if (defaults.show_border) {
    if (typeof defaults.border_width === 'undefined' || defaults.border_width === null) {
      defaults.border_width = 2;
    }
    if (typeof defaults.border_margin === 'undefined' || defaults.border_margin === null) {
      defaults.border_margin = 0;
    }
    if (!defaults.border_color) {
      defaults.border_color = '000000';
    }
  }

  if (siteSettings.canOverride && siteSettings.site_theme) {
    const theme_overide = siteSettings.site_theme;

    defaults.font_id = theme_overide?.popup_font_family || defaults.font_id;
    defaults.background_color =
      theme_overide?.popup_background_color || defaults.background_color;
    defaults.text_color =
      theme_overide?.popup_text_color || defaults.text_color;
    defaults.cross_color = theme_overide?.cross_color || defaults.cross_color;
    defaults.text_field_background_color =
      theme_overide?.text_field_background_color ||
      defaults.text_field_background_color;
    defaults.text_field_text_color =
      theme_overide?.text_field_text_color || defaults.text_field_text_color;
    defaults.text_field_border_color =
      theme_overide?.text_field_border_color ||
      defaults.text_field_border_color;
    defaults.text_field_border_width =
      theme_overide?.text_field_border_width ||
      defaults.text_field_border_width;
    defaults.text_field_font_size =
      theme_overide?.text_field_font_size || defaults.text_field_font_size;
    defaults.text_field_font_family =
      theme_overide?.text_field_font_family || defaults.text_field_font_family;
    defaults.button_color =
      theme_overide?.cta_button_color || defaults.button_color;
    defaults.cta_border_color =
      theme_overide?.cta_border_color || defaults.cta_border_color;
    defaults.cta_border_width =
      theme_overide?.cta_border_width || defaults.cta_border_width;
    defaults.cta_height = theme_overide?.cta_height || defaults.cta_height;
    defaults.link_color = theme_overide?.cta_text_color || defaults.link_color;
    defaults.cta_shadow_enabled =
      theme_overide?.cta_shadow_enabled || defaults.cta_shadow_enabled;
    defaults.conversion_font_size =
      theme_overide?.cta_font_size || defaults.conversion_font_size;
  }

  // For form popups, preserve the current button_color (set from form template defaults
  // or by the user) so site-wide theme overrides don't clobber it.
  if (settings.subtype === 'forms' && settings.button_color) {
    defaults.button_color = settings.button_color;
  }

  // ALWAYS preserve existing border-related values from current settings when available
  // This ensures user's saved border settings are never overwritten by theme defaults
  const preservedBorderSettings = {};

  if (settings.show_border !== undefined && settings.show_border !== null) {
    preservedBorderSettings.show_border = Boolean(settings.show_border);
  } else if (typeof defaults.show_border === 'undefined') {
    preservedBorderSettings.show_border = false;
  }

  if (settings.border_color !== undefined && settings.border_color !== null) {
    preservedBorderSettings.border_color = settings.border_color;
  }

  if (settings.border_width !== undefined && settings.border_width !== null) {
    preservedBorderSettings.border_width = settings.border_width;
  }

  if (settings.border_margin !== undefined && settings.border_margin !== null) {
    preservedBorderSettings.border_margin = settings.border_margin;
  }

  // ALWAYS preserve existing shadow-related values from current settings when available
  // This ensures user's saved shadow settings are never overwritten by theme defaults
  const preservedShadowSettings = {};

  // Always preserve show_shadow if it exists in settings (even if false)
  if (settings.show_shadow !== undefined && settings.show_shadow !== null) {
    preservedShadowSettings.show_shadow = Boolean(settings.show_shadow);
  }

  // Always preserve shadow_color if it exists in settings
  if (settings.shadow_color !== undefined && settings.shadow_color !== null) {
    preservedShadowSettings.shadow_color = settings.shadow_color;
  } else if (preservedShadowSettings.show_shadow) {
    // If shadow is enabled but no color is set, use default black
    preservedShadowSettings.shadow_color = '000000';
  }

  // IMPORTANT: preserve non-theme configuration blobs (like gamification) that aren't part of theme defaults.
  // Theme changes should not wipe these settings, otherwise saving/publishing can drop them.
  let gamificationOverride = settings.gamification ? { gamification: settings.gamification } : {};
  if (settings.gamification?.rewards?.length && settings.theme_id) {
    const themeDefs = getGamificationThemeDefaults(settings.theme_id);
    const segmentColors = themeDefs.spin?.segmentColors;
    if (segmentColors?.length) {
      gamificationOverride = {
        gamification: {
          ...settings.gamification,
          rewards: settings.gamification.rewards.map((reward, i) => ({
            ...reward,
            color: segmentColors[i % segmentColors.length],
          })),
        },
      };
    }
  }
  return {
    ...reset,
    ...defaults,
    ...preservedBorderSettings,
    ...preservedShadowSettings,
    image_placement: imagePlacement(),
    ...gamificationOverride,
  };
};

function reducer(state, action) {
  const actionUpdate = (payload) => {
    if (payload.action_link) {
      payload.link_text = payload.action_link;
    }
    if (payload.background_color) {
      payload.primary_color = payload.background_color;
    }
    if (payload.button_color) {
      payload.secondary_color = payload.button_color;
    }

    return { ...state, ...payload };
  };

  const actionUpdateGoal = (payload) => {
    if (payload === state.element_subtype) {
      return actionUpdate({});
    }

    const goalDefaults = reduceOnSelectGoal(payload) || {};
    return actionUpdate({
      type: 'Bar',
      element_subtype: payload,
      subtype: payload,
      ...goalDefaults,
    });
  };

  const actionUpdateType = (payload, siteSettingsFromAction) => {
    if (payload === state.type) {
      return actionUpdate({});
    }
    // Pass current state type to check if we're switching FROM Inline
    // siteSettings is not typically passed with type updates, so we'll handle gracefully
    // Theme default borders in CSS (like techcrunch) are not affected by showBorder setting
    const siteSettings = siteSettingsFromAction;
    return actionUpdate(reduceOnSelectType(payload, state.type, state.theme_id, siteSettings));
  };

  const actionUpdateTheme = (payload) => {
    state.font = state.font_id; // force set font so that it can be used in preview

    if (payload.theme_id === state.theme_id) {
      return actionUpdate({});
    }
    if (!payload.siteSettings) {
      return { ...state };
    }
    if (payload.theme_id) {
      state.theme_id = payload.theme_id;
      const theme = availableThemes(payload.siteSettings, state.type).find(
        ({ id }) => id === payload.theme_id
      );
      state.font_id = theme.defaults[state.type]?.font_id || state.font_id;
      state.font = payload.siteSettings.fonts[state.font_id];
      state.theme = theme;
    }
    if (payload.theme_id === 'autodetect') {
      const defaults = reduceTheme(state, payload.siteSettings);

      return actionUpdate({ ...defaults, ...payload.autodetectTheme.theme });
    }
    return actionUpdate(reduceTheme(state, payload.siteSettings));
  };

  function update(action) {
    switch (action.type) {
      case 'update':
        return actionUpdate(action.payload);
      case 'update_goal':
        return actionUpdateGoal(action.payload);
      case 'update_type':
        return actionUpdateType(action.payload, action.siteSettings);
      case 'update_theme':
        return actionUpdateTheme(action.payload);
      case 'update_gamification':
        return { ...state, gamification: { ...state.gamification, ...action.payload } };
      default:
        throw new Error(`action type "${action.type}" is not valid`);
    }
  }

  return update(action);
}

const useSettingsReducer = (initialSettings) => {
  // Normalize boolean values (handle null, undefined, 0, 1, "true", "false")
  if (initialSettings.show_border !== undefined && initialSettings.show_border !== null) {
    initialSettings.show_border = Boolean(initialSettings.show_border);
  } else {
    initialSettings.show_border = false;
  }

  if (initialSettings.show_shadow !== undefined && initialSettings.show_shadow !== null) {
    initialSettings.show_shadow = Boolean(initialSettings.show_shadow);
  } else {
    initialSettings.show_shadow = false;
  }

  // Ensure shadow_color has a default value if show_shadow is true
  if (initialSettings.show_shadow && (!initialSettings.shadow_color || initialSettings.shadow_color === null || initialSettings.shadow_color === undefined)) {
    initialSettings.shadow_color = '000000';
  }

  // Normalize CTA hover and typography fields
  const ctaFields = [
    'cta_bold', 'cta_italic', 'cta_underline', 'cta_shadow_enabled',
    'cta_hover_bold', 'cta_hover_italic', 'cta_hover_underline'
  ];
  ctaFields.forEach(field => {
    if (initialSettings[field] !== undefined && initialSettings[field] !== null) {
      initialSettings[field] = initialSettings[field] === true || initialSettings[field] === 1 || initialSettings[field] === 'true' || initialSettings[field] === '1';
    }
  });

  if (initialSettings.show_optional_caption && !initialSettings.caption) {
    initialSettings.show_optional_caption = false;
  }
  if (initialSettings.show_optional_content && !initialSettings.content) {
    initialSettings.show_optional_content = false;
  }

  const isFormsInitial = initialSettings.subtype === 'forms' || initialSettings.element_subtype === 'forms';
  if (isFormsInitial && (initialSettings.border_radius == null || initialSettings.border_radius === 0)) {
    initialSettings.border_radius = 12;
  }
  if (isFormsInitial && !initialSettings.thank_you_text) {
    initialSettings.thank_you_text = 'Thank you for your response!';
  }

  if (!initialSettings.media_type) initialSettings.media_type = 'image';
  if (!initialSettings.video_source_type) initialSettings.video_source_type = 'url';
  if (initialSettings.video_source_type === 'upload') initialSettings.video_source_type = 'url';
  if (typeof initialSettings.video_autoplay === 'undefined' || initialSettings.video_autoplay === null) {
    initialSettings.video_autoplay = false;
  }
  if (typeof initialSettings.video_muted === 'undefined' || initialSettings.video_muted === null) {
    initialSettings.video_muted = true;
  }
  if (typeof initialSettings.video_loop === 'undefined' || initialSettings.video_loop === null) {
    initialSettings.video_loop = false;
  }
  if (typeof initialSettings.video_controls === 'undefined' || initialSettings.video_controls === null) {
    initialSettings.video_controls = true;
  }

  return useReducer(reducer, initialSettings);
};

export { useSettingsReducer };
